import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import c from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import DAProtectIconWithTooltip from '@wix/da-shared-react/pkg/DAProtectIconWithTooltip';
import WidgetHeader from '../../../UserWidgets/_partials/WidgetHeader/WidgetHeader';
import WidgetHeaderMobile from '../../../UserWidgets/_partials/WidgetHeaderMobile/WidgetHeaderMobile';
import EditGallectionButton from '../EditGallectionButton';
import SearchField from '../SearchField';
import { GallectionType } from '../../../../../types/gallection';
import { isCurrentUserProfileOwner } from '../../../../selectors/users';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useGallectionFolders } from '../hooks/useGallectionFolders';
import {
  setSelectedFolderId,
  unsetSelectedSubfolderId,
} from '../../../../actions/gallectionSection';
import { ALL_FOLDER_ID } from '@wix/da-url';
import ArrowLongLeft from '@wix/da-ds/pkg/Icons/ArrowLongLeft';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';

import s from './FoldersHeader.scss';

export interface Props {
  gallectionType: GallectionType;
  isFolderSliderHidden?: boolean;
  className?: string;
}

export const FoldersHeader: FC<Props> = ({
  gallectionType,
  isFolderSliderHidden,
  className,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isGallery = gallectionType === GallectionType.GALLERY;
  const hasCore = useSelector(getCurrentUserHasCore);
  const isOwner = useSelector(isCurrentUserProfileOwner);

  const { folders, selectedFolderId } = useGallectionFolders();
  const selectedFolderIndex = useMemo(() => {
    return folders.findIndex(({ folderId }) => folderId === selectedFolderId);
  }, [folders, selectedFolderId]);

  const selectedFolder = useMemo(
    () => folders[selectedFolderIndex],
    [folders, selectedFolderIndex]
  );

  const showBackToGalleries = useMemo(() => {
    return selectedFolder && selectedFolder.hasSubfolders;
  }, [selectedFolder]);

  const header = isFolderSliderHidden
    ? ''
    : t(`pages.gallections.foldersHeader.var.${gallectionType}`);

  if (isMobile) {
    return <WidgetHeaderMobile header={header} />;
  }

  return (
    <div className={s['root']}>
      {showBackToGalleries && (
        <TextButton
          icon={ArrowLongLeft}
          iconSizeOverride={IconSize.LARGE}
          variant="main"
          size="large"
          onClick={() => {
            dispatch(unsetSelectedSubfolderId());
            dispatch(setSelectedFolderId(ALL_FOLDER_ID));
          }}
          className={c(s['header'], s['backToGalleries'], className)}
        >
          {t('pages.gallections.foldersHeader.backToGalleries')}
        </TextButton>
      )}
      {!showBackToGalleries && (
        <WidgetHeader
          header={header}
          className={c(
            s['header'],
            isFolderSliderHidden && s['folder-slider-hidden'],
            className
          )}
          extras={
            <div className={s['header-extras']}>
              {isGallery && isOwner && hasCore && (
                <DAProtectIconWithTooltip
                  variant="active"
                  className={s['da-protect-icon']}
                />
              )}
              <EditGallectionButton className={s['edit-button']} />
              {/* Search Field shows in lower header (FolderBar) when folder slider is hidden */}
              {!isFolderSliderHidden && (
                <SearchField className={s['flex-grow-right']} />
              )}
            </div>
          }
        />
      )}
    </div>
  );
};
FoldersHeader.displayName = 'FoldersHeader';

export default FoldersHeader;
